import React from 'react';
import { FileError } from 'react-dropzone';
import { AlertIcon, Box, CheckBox, ErrorModal, Inscription, Preloader, TextAreaInput } from '@common-fe/common-fe';
import styled from 'styled-components';

import { allowedExtensions, allowedFileTypes, SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import REGEXPS from '@/common/regexp';
import AppButton from '@/components/controls/AppButton';
import PreviewPaymentDocument from '@/modules/employee/TransactionDetails/components/TransactionPaymentDetails/components/PreviewPaymentDocument/PreviewPaymentDocument';

import { useCombineHSAConfirm } from './hooks/useCombineHSAConfirm';
import { CombineHSASectionType, HSACombineAccount } from './types/combineHSA.types';
import { CombineHSAEmployeeCard } from './CombineHSAEmployeeCard';

interface Props {
  selectedFromAccount: HSACombineAccount;
  selectedToAccount: HSACombineAccount;
  employeeId: string;
  onBack: () => void;
  onSuccess: () => void;
  onFileError: (error: FileError | null) => void;
}

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 40% 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacings.spacing16};
`;

const FormRowTop = styled(FormRow)`
  align-items: start;
`;

const OutlineUploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 124px;
  padding: ${({ theme }) => `${theme.spacings.spacing8} ${theme.spacings.spacing16}`};
  border-radius: ${({ theme }) => theme.rounds.button1Round};
  border: 2px solid ${({ theme }) => theme.colors.accentActive};
  background: transparent;
  cursor: pointer;
  transition: background 0.3s, border-color 0.3s;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.accentContainer};
    border-color: ${({ theme }) => theme.colors.accentActive};
  }
  &:active {
    background: ${({ theme }) => theme.colors.accentBorder};
    border-color: ${({ theme }) => theme.colors.accentActive};
  }
`;

export const CombineHSAConfirmStep: React.FC<Props> = ({
  selectedFromAccount,
  selectedToAccount,
  employeeId,
  onBack,
  onSuccess,
  onFileError,
}) => {
  const {
    documentFileUrl,
    isDocPreviewModalVisible,
    onDocPreviewOpen,
    onDocPreviewClose,
    isUploading,
    isSubmitting,
    isServerError,
    setIsServerError,
    note,
    sendEmail,
    linkAccounts,
    isDocumentError,
    onFileSelect,
    onNoteChange,
    onSendEmailChange,
    onLinkAccountsChange,
    onSubmit,
  } = useCombineHSAConfirm({ employeeId, selectedFromAccount, selectedToAccount, onFileError, onSuccess });

  return (
    <Box direction="column" gap="spacing20" width="100%">
      <Box
        direction="column"
        gap="spacing20"
        pad="spacing20"
        background="module"
        border={{ size: 'xxsmall', color: 'border2' }}
        round="moduleRound"
      >
        <Box direction="column" gap="spacing16">
          <Box direction="row" align="center" gap="spacing12">
            <Inscription weight="bold" color="textTitle" lineHeight="24px">
              Transfer from:
            </Inscription>
            <Box
              background="dangerContainer"
              round="999px"
              pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
            >
              <Inscription size="small" weight="bold" color="danger" lineHeight="18px">
                Will be closed
              </Inscription>
            </Box>
          </Box>
          <CombineHSAEmployeeCard
            account={selectedFromAccount}
            sectionType={CombineHSASectionType.FROM}
            hideRadio
            defaultExpanded
          />
        </Box>
        <Box align="center" justify="center">
          <Inscription size="2xl">↓</Inscription>
        </Box>
        <Box direction="column" gap="spacing16">
          <Box direction="row" align="center" gap="spacing12">
            <Inscription weight="bold" color="textTitle" lineHeight="24px">
              Transfer to:
            </Inscription>
            <Box
              background="successContainer"
              round="999px"
              pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
            >
              <Inscription size="small" weight="bold" color="success" lineHeight="18px">
                Receiving funds
              </Inscription>
            </Box>
          </Box>
          <CombineHSAEmployeeCard
            account={selectedToAccount}
            sectionType={CombineHSASectionType.TO}
            hideRadio
            defaultExpanded
          />
        </Box>
      </Box>
      <Box
        direction="column"
        gap="spacing20"
        pad="spacing20"
        background="module"
        round="moduleRound"
      >
        <Box
          direction="column"
          gap="spacing20"
          pad="spacing20"
          background="canvas"
          border={{ size: 'small', color: 'border2' }}
          round="moduleRound"
        >
          <FormRow data-testid="combine-hsa-confirm_document-row">
            <Box direction="row" align="center" gap="spacing4">
              <Inscription weight="bold" color="textTitle" lineHeight="20px">
                Document
              </Inscription>
              <Inscription color="danger" weight="bold" lineHeight="20px">
                *
              </Inscription>
            </Box>
            <Box direction="column" gap="spacing8">
              <Box direction="row" align="center" gap="spacing12" height={{ min: '74px' }}>
                <OutlineUploadLabel
                  htmlFor="combine-hsa-confirm-file-upload"
                  data-testid="combine-hsa-confirm_upload-label"
                  style={{ pointerEvents: isUploading ? 'none' : 'auto', opacity: isUploading ? 0.5 : 1 }}
                >
                  <Inscription size="medium" weight="bold" color="textBody" lineHeight="20px">
                    {documentFileUrl ? 'Change' : 'Upload'}
                  </Inscription>
                  <input
                    id="combine-hsa-confirm-file-upload"
                    type="file"
                    accept={`${allowedExtensions.join(',')},${allowedFileTypes.join(',')}`}
                    style={{ display: 'none' }}
                    onChange={onFileSelect}
                    data-testid="combine-hsa-confirm_file-input"
                  />
                </OutlineUploadLabel>
                {isUploading && <Preloader testId="combine-hsa-confirm_preloader" />}
                {!isUploading && documentFileUrl && (
                  <Box data-testid="combine-hsa-confirm_file-preview" width={{ max: '74px' }}>
                    <PreviewPaymentDocument
                      key={documentFileUrl}
                      paymentDocumentPath={documentFileUrl}
                      modalTitle="File Preview"
                      isNumberOfImageHidden
                      isDocPreviewModalVisible={isDocPreviewModalVisible}
                      setIsDocPreviewModalVisible={(visible) => visible ? onDocPreviewOpen() : onDocPreviewClose()}
                    />
                  </Box>
                )}
              </Box>
              {isDocumentError && (
                <Box
                  data-testid="combine-hsa-confirm_document-error"
                  direction="row"
                  align="center"
                  gap="spacing4"
                >
                  <AlertIcon size="20px" />
                  <Inscription color="danger" lineHeight="20px">
                    The document is required
                  </Inscription>
                </Box>
              )}
            </Box>
          </FormRow>
          <FormRowTop data-testid="combine-hsa-confirm_note-row">
            <Inscription weight="bold" color="textTitle" lineHeight="20px">
              Notes
            </Inscription>
            <Box direction="column" gap="spacing4">
              <TextAreaInput
                data-testid="combine-hsa-confirm_note-textarea"
                placeholder="Enter note"
                value={note}
                onChange={onNoteChange}
              />
              <Inscription
                data-testid="combine-hsa-confirm_note-counter"
                size="small"
                color="textSecondary"
                lineHeight="16px"
              >
                {note.replace(REGEXPS.NEWLINE, '').length}/2000
              </Inscription>
            </Box>
          </FormRowTop>
          <FormRow
            data-testid="combine-hsa-confirm_send-email-checkbox"
            style={{ cursor: 'pointer' }}
            onClick={() => onSendEmailChange(!sendEmail)}
          >
            <Inscription color="textBody" lineHeight="20px" weight="bold">
              Send email notification to employee
            </Inscription>
            <CheckBox data-testid="combine-hsa-confirm_send-email-checkbox-input" checked={sendEmail} />
          </FormRow>
          <FormRow
            data-testid="combine-hsa-confirm_link-accounts-checkbox"
            style={{ cursor: 'pointer' }}
            onClick={() => onLinkAccountsChange(!linkAccounts)}
          >
            <Inscription color="textBody" lineHeight="20px" weight="bold">
              Link accounts after transfer
            </Inscription>
            <CheckBox data-testid="combine-hsa-confirm_link-accounts-checkbox-input" checked={linkAccounts} />
          </FormRow>
        </Box>
      </Box>
      <ErrorModal
        testId="combine-hsa-confirm_server-error"
        visible={isServerError}
        header={SERVER_ERROR_MODAL_TITLE}
        helpText={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        onSetVisible={setIsServerError}
      />
      <Box direction="column" margin={{ top: 'spacing4' }}>
        <Box direction="row" justify="end" gap="spacing12">
          <AppButton
            data-testid="combine-hsa-confirm_back-button"
            buttonType="secondary"
            width="160px"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </AppButton>
          <AppButton
            data-testid="combine-hsa-confirm_submit-button"
            width="160px"
            onClick={onSubmit}
            disabled={isUploading || isSubmitting}
          >
            Submit (2/2)
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};
